import { useState, type ReactElement } from 'react';
import type { BoardCalendarFeed } from '@molt/shared';
import { Button, Dialog, Notice, TextField } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface BoardCalendarFeedSectionProps {
  readonly feed: BoardCalendarFeed | undefined;
  readonly loading?: boolean | undefined;
  readonly loadError?: string | undefined;
  readonly canEnable: boolean;
  readonly disabledReason: string | null;
  readonly tokenBusy?: boolean | undefined;
  readonly enabledBusy?: boolean | undefined;
  readonly tokenError?: string | undefined;
  readonly enabledError?: string | undefined;
  readonly onGenerateToken: () => void;
  readonly onSetEnabled: (enabled: boolean) => void;
}

export function BoardCalendarFeedSection({
  feed,
  loading = false,
  loadError,
  canEnable,
  disabledReason,
  tokenBusy,
  enabledBusy,
  tokenError,
  enabledError,
  onGenerateToken,
  onSetEnabled,
}: BoardCalendarFeedSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const checking = loading || (feed === undefined && loadError === undefined);
  const feedUrl = feed?.feedUrl ?? '';
  const hasUrl = feedUrl !== '';
  const clipboardAvailable =
    typeof navigator !== 'undefined' && navigator.clipboard?.writeText !== undefined;
  const enabledDisabledReason = checking
    ? t('integrations.calendarFeed.reason.checking')
    : (loadError ??
      (canEnable
        ? null
        : (disabledReason ?? t('integrations.calendarFeed.reason.adminOnly'))));
  const tokenDisabledReason = checking
    ? t('integrations.calendarFeed.reason.checking')
    : (loadError ?? null);
  const copyDisabledReason = !hasUrl
    ? t('integrations.calendarFeed.reason.noUrl')
    : clipboardAvailable
      ? null
      : t('integrations.calendarFeed.reason.clipboardUnavailable');
  const enabledBusyProps = enabledBusy === undefined ? {} : { busy: enabledBusy };
  const tokenBusyProps = tokenBusy === undefined ? {} : { busy: tokenBusy };

  async function copyUrl(): Promise<void> {
    if (!hasUrl || !clipboardAvailable) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopyError(null);
      setCopyMessage(t('integrations.calendarFeed.announce.copied'));
    } catch {
      setCopyMessage(null);
      setCopyError(t('integrations.calendarFeed.announce.copyFailed'));
    }
  }

  function requestToken(): void {
    setCopyMessage(null);
    setCopyError(null);
    if (hasUrl) {
      setResetConfirmOpen(true);
      return;
    }
    onGenerateToken();
  }

  function confirmReset(): void {
    if (tokenBusy === true) return;
    setCopyMessage(null);
    setCopyError(null);
    onGenerateToken();
    setResetConfirmOpen(false);
  }

  return (
    <>
      <section aria-labelledby="calendar-feed-heading" className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 id="calendar-feed-heading" className="text-sm font-semibold text-ink">
            {t('integrations.calendarFeed.heading')}
          </h3>
          <p className="text-sm text-ink-muted">
            {t('integrations.calendarFeed.description')}
          </p>
        </div>

        {checking ? (
          <Notice tone="info" body={t('integrations.calendarFeed.checkingNotice')} />
        ) : loadError !== undefined ? (
          <Notice tone="bad" body={loadError} />
        ) : feed === undefined ? (
          <Notice tone="bad" body={t('integrations.calendarFeed.loadFailed')} />
        ) : (
          <>
            {hasUrl && feed.enabled === false ? (
              <Notice tone="info" body={t('integrations.calendarFeed.disabledNotice')} />
            ) : null}
            <TextField
              label={t('integrations.calendarFeed.urlLabel')}
              readOnly
              value={feedUrl}
            />
          </>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="toggle"
            role="switch"
            aria-checked={feed?.enabled === true}
            aria-label={t('integrations.calendarFeed.toggleLabel')}
            disabled={enabledDisabledReason !== null}
            disabledReason={enabledDisabledReason ?? undefined}
            onClick={() => {
              if (feed !== undefined) onSetEnabled(!feed.enabled);
            }}
            {...enabledBusyProps}
          >
            {feed?.enabled === true
              ? t('integrations.calendarFeed.toggleOn')
              : t('integrations.calendarFeed.toggleOff')}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={tokenDisabledReason !== null}
            disabledReason={tokenDisabledReason ?? undefined}
            onClick={requestToken}
            {...tokenBusyProps}
          >
            {hasUrl
              ? t('integrations.calendarFeed.resetAction')
              : t('integrations.calendarFeed.generateAction')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={copyDisabledReason !== null}
            disabledReason={copyDisabledReason ?? undefined}
            onClick={() => {
              void copyUrl();
            }}
          >
            {t('integrations.calendarFeed.copyAction')}
          </Button>
        </div>

        {tokenError !== undefined ? <Notice tone="bad" body={tokenError} /> : null}
        {enabledError !== undefined ? <Notice tone="bad" body={enabledError} /> : null}
        {copyMessage !== null ? <Notice tone="info" body={copyMessage} /> : null}
        {copyError !== null ? <Notice tone="bad" body={copyError} /> : null}
      </section>
      <Dialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        size="sm"
        title={t('integrations.calendarFeed.resetDialog.title')}
        description={t('integrations.calendarFeed.resetDialog.description')}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setResetConfirmOpen(false)}
            >
              {t('integrations.calendarFeed.resetDialog.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={confirmReset}
              {...tokenBusyProps}
            >
              {t('integrations.calendarFeed.resetDialog.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">
          {t('integrations.calendarFeed.resetDialog.body')}
        </p>
      </Dialog>
    </>
  );
}
